import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";
import { ParlorDetailContainer } from "@/containers/ParlorDetailContainer";

import parlors from "@/constants/parlors_detail.json";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import { buildParlorPageProps } from "@/utils/buildParlorPageProps";
import { ParlorDetailProps } from "@/types/parlor";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;

  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = parlors.map((parlor) => ({
    params: {
      id: parlor.id.toString(),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const parlorId = parseInt(params?.id as string);
  const {
    parlor,
    nearbyParlors,
    sidoName,
    sigunguName,
    companyName,
    description,
    mainImage,
    jsonLd,
  } = buildParlorPageProps(parlorId);

  const title = `${companyName} | 비용, 위치, 정보, 고이 장례식장 정보`;

  const keywords = [
    companyName,
    `${companyName} 장례식장`,
    `${companyName} 비용`,
    `${companyName} 빈소`,
    `${companyName} 예약`,
    `${companyName} 시설`,
    `${sigunguName} 장례식장`,
    `${sigunguName} 장례식장 추천`,
    `${sigunguName} 장례식장 비용`,
    `${sigunguName} 장례식장 예약`,
    `${sigunguName} 장례식장 위치`,
    `${sigunguName} 장례식장 정보`,
    `${sigunguName} 장례식장 시설`,
    `${sigunguName} 빈소`,
    `${sigunguName} 빈소 비용`,
    `${sigunguName} 장례 비용`,
    `${sigunguName} 장례`,
    `${sigunguName} 장례식`,
    `${sidoName} 장례식장`,
    `${sidoName} 장례식장 추천`,
    `${sidoName} 장례식장 비용`,
    `${sidoName} 장례식장 예약`,
    `${sidoName} 장례식장 정보`,
    `${sidoName} 빈소`,
    `${sidoName} 장례 비용`,
    `${sidoName} 장례`,
    "장례식장",
    "장례식장 추천",
    "장례식장 비용",
    "장례식장 예약",
    "장례식장 정보",
    "장례식장 위치",
    "장례식장 시설",
    "장례식장 시설 사용료",
    "장례식장 빈소",
    "빈소",
    "빈소 비용",
    "빈소 현황",
    "빈소 예약",
    "빈소 가격",
    "장례 비용",
    "장례 비용 안내",
    "장례 비용 견적",
    "장례 절차",
    "장례 상담",
    "장례 예약",
    "장례 준비",
    "장례식",
    "장례식 비용",
    "장례식 준비",
    "장례 서비스",
    "장례 안내",
    "무료 장례 상담",
    "24시간 장례 상담",
    "장례 견적",
    "장례 견적 비교",
    "장례 전문",
    "장례 전문 업체",
    "병원 장례식장",
    "공영 장례식장",
    "민간 장례식장",
    "장례식장 종류",
    "장례식장 선택",
    "장례식장 비교",
    "시설 사용료",
    "장례 용품",
    "장례 차량",
    "장례 인력",
    "상조 서비스",
    "상조 비교",
    "고이장례연구소",
    "고이 장례",
    "고이 장례 상담",
    "e하늘 장사정보시스템",
    "장사정보시스템",
    "장례식장 가격",
    "저렴한 장례식장",
    "좋은 장례식장",
    "가까운 장례식장",
    "근처 장례식장",
    "인근 장례식장",
    "지역 장례식장",
    "장례 후기",
    "장례식장 후기",
    "장례 만족도",
    "장례 투명",
    "숨김없는 장례",
    "장례 안심",
  ]
    .filter(Boolean)
    .join(", ");

  return {
    props: {
      defaultParlor: parlor,
      nearbyParlors,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage:
        mainImage || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      pageJsonLd: JSON.stringify(jsonLd),
      pageCanonical: `https://parlor.goifuneral.co.kr/place/${parlorId}/family/main/`,
    },
  };
};

interface ParlorFamilyMainPageProps {
  defaultParlor: ParlorDetailProps;
  nearbyParlors: Array<{ id: number; companyname: string; sido: string }>;
  pageJsonLd?: string;
}

const ParlorFamilyMainPage = ({
  defaultParlor,
  nearbyParlors,
  pageJsonLd,
}: ParlorFamilyMainPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <ParlorDetailContainer
        defaultParlor={defaultParlor}
        nearbyParlors={nearbyParlors}
      />
    </>
  );
};

export default ParlorFamilyMainPage;
